import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetch_vendor_bookings,
  fetch_vendor_summary,
  act_on_booking,
  reschedule_booking,
  set_vendor_notes,
  send_walk_otp,
  verify_walk_otp,
  override_walk_end,
  fetch_walker_settings,
  update_walker_settings,
  type VendorBookingAction,
  type WalkOtpKind,
} from './api';
import type { BookingStatus } from './types';

const VENDOR_BOOKINGS_KEY = ['vendor-bookings'] as const;
const VENDOR_SUMMARY_KEY = ['vendor-bookings', 'summary'] as const;

export function use_vendor_bookings(status?: BookingStatus, date?: string) {
  return useQuery({
    queryKey: [...VENDOR_BOOKINGS_KEY, status ?? 'all', date ?? 'any'],
    queryFn: () => fetch_vendor_bookings(status, date),
  });
}

export function use_vendor_summary() {
  return useQuery({ queryKey: VENDOR_SUMMARY_KEY, queryFn: fetch_vendor_summary });
}

function use_invalidate_bookings() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: VENDOR_BOOKINGS_KEY });
}

export function use_booking_action() {
  const invalidate = use_invalidate_bookings();
  return useMutation({
    mutationFn: ({ id, action, reason }: { id: string; action: VendorBookingAction; reason?: string }) =>
      act_on_booking(id, action, reason),
    onSuccess: invalidate,
  });
}

export function use_reschedule_booking() {
  const invalidate = use_invalidate_bookings();
  return useMutation({
    mutationFn: ({ id, slot_start }: { id: string; slot_start: string }) => reschedule_booking(id, slot_start),
    onSuccess: invalidate,
  });
}

export function use_set_vendor_notes() {
  const invalidate = use_invalidate_bookings();
  return useMutation({
    mutationFn: ({ id, vendor_notes }: { id: string; vendor_notes: string }) => set_vendor_notes(id, vendor_notes),
    onSuccess: invalidate,
  });
}

// ---- Walk-session OTP ----
/** Sends a start/end code to the owner. Does not touch the bookings cache (no status change yet). */
export function use_send_walk_otp() {
  return useMutation({
    mutationFn: ({ id, kind }: { id: string; kind: WalkOtpKind }) => send_walk_otp(id, kind),
  });
}

/** Verifies a code → advances the walk; refreshes the bookings list. */
export function use_verify_walk_otp() {
  const invalidate = use_invalidate_bookings();
  return useMutation({
    mutationFn: ({ id, kind, code }: { id: string; kind: WalkOtpKind; code: string }) => verify_walk_otp(id, kind, code),
    onSuccess: invalidate,
  });
}

/** Completes a walk without the end code (owner unavailable). */
export function use_override_walk_end() {
  const invalidate = use_invalidate_bookings();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => override_walk_end(id, reason),
    onSuccess: invalidate,
  });
}

// ---- Walker settings (walk-OTP toggle) ----
const WALKER_SETTINGS_KEY = ['walker-settings'] as const;

/** Reads the walker's require_walk_otp flag. `enabled` guards non-walker vendors (404s otherwise). */
export function use_walker_settings(enabled = true) {
  return useQuery({ queryKey: WALKER_SETTINGS_KEY, queryFn: fetch_walker_settings, enabled });
}

export function use_update_walker_settings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (require_walk_otp: boolean) => update_walker_settings(require_walk_otp),
    onSuccess: (data) => qc.setQueryData(WALKER_SETTINGS_KEY, data),
  });
}
